import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff } from 'lucide-react';

export default function CandidateLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginCandidate } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await loginCandidate(formData.email, formData.password);
    
    if (result.verificationRequired) {
      navigate('/verify-email', { state: { email: result.email, userType: result.userType } });
      return;
    }
    if (result.success) {
      // Wait a bit for cookies to be set before navigating
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/candidate/profile');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="mb-6">
            <h1 className="text-5xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
                Trab
              </span>
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                adju
              </span>
            </h1>
            <p className="text-xs text-gray-500 tracking-widest uppercase mt-1">Cabo Verde</p>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Login Candidato</h2>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="candidate-login-form">
          <div>
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="candidate-login-email"
            />
          </div>

          <div>
            <Label htmlFor="password">{t('auth.password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="candidate-login-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={loading}
            data-testid="candidate-login-btn"
          >
            {loading ? t('common.loading') : t('auth.loginButton')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <span className="text-gray-600">{t('auth.noAccount')} </span>
          <Link to="/candidate/register" className="text-blue-600 hover:text-blue-700 font-medium">
            {t('auth.registerButton')}
          </Link>
        </div>

        <div className="mt-3 text-center text-sm">
          <Link to="/forgot-password" className="text-gray-500 hover:text-blue-600 underline" data-testid="forgot-password-link">
            Esqueci-me da palavra-passe
          </Link>
        </div>

        <div className="mt-4 text-center">
          <Link to="/start" className="text-sm text-gray-500 hover:text-gray-700">
            ← Voltar
          </Link>
        </div>
      </Card>
    </div>
  );
}
